// Fetches live asset rates and normalises the new backend shape into
// a flat, stable object every component can depend on.
//
// New backend (getLiveRates) returns:
//   { data: [{ symbol, name, priceNgn, priceUsd, change24h, changeType, image, networks }] }
//
// This hook exposes:
//   assets        — array of normalised AssetInfo objects (one per symbol)
//   priceMap      — { USDT: { priceNgn, priceUsd, change24h, image, name }, … }
//   loading       — true only on the very first fetch
//   error         — string | null
//   refresh()     — manual refetch

import { useState, useEffect, useRef, useCallback } from "react";
import { getAssets } from "../api/apiServices";

// ── Normalise one raw asset from the API ──────────────────────
function normalise(raw) {
  return {
    symbol:      raw.symbol     || "",
    name:        raw.name       || raw.symbol || "",
    priceNgn:    Number(raw.priceNgn  || 0),
    priceUsd:    Number(raw.priceUsd  || 0),
    change24h:   Number(raw.change24h || 0),
    changeType:  raw.change24h >= 0 ? "positive" : "negative",
    image:       raw.image      || "",
    networks:    raw.networks   || [], // [{ network, contractAddress, decimals }]
  };
}

export function useAssets({ pollInterval = 60_000 } = {}) {
  const [assets, setAssets]   = useState([]); // AssetInfo[]
  const [priceMap, setPriceMap] = useState({}); // { symbol: AssetInfo }
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const initialised           = useRef(false);
  const timerRef              = useRef(null);

  const fetch_ = useCallback(async () => {
    if (!initialised.current) setLoading(true);
    setError(null);
    try {
      const res = await getAssets();
      const raw = Array.isArray(res) ? res : (res?.data || []);
      const normalised = raw.map(normalise);

      setAssets(normalised);
      setPriceMap(Object.fromEntries(normalised.map(a => [a.symbol, a])));
    } catch (err) {
      setError(err?.message || "Failed to load rates");
    } finally {
      setLoading(false);
      initialised.current = true;
    }
  }, []);

  useEffect(() => {
    fetch_();
    if (pollInterval > 0) {
      timerRef.current = setInterval(fetch_, pollInterval);
    }
    return () => clearInterval(timerRef.current);
  }, [fetch_, pollInterval]);

  return { assets, priceMap, loading, error, refresh: fetch_ };
}
